// components
import { Tooltip } from '@mui/material';
import { Icon, IconButton } from '@sdb/planning-ui';
import { PulsingBadge } from 'components/shared/Badge/PulsingBadge';
import { StyledRelativeBox } from 'components/shared/Box/Box.styles';

// hooks
import { useTranslation } from 'react-i18next';
import { useIsVerticalToolbar } from 'components/shared/ToolbarSet/ToolbarPosition.context';

// utils
import { isAutomaticRosteringInProgress } from '../automaticRostering.utils';

// types
import type { AutomaticRosteringActionsProps } from './AutomaticRosteringActions.types';

// styles
import {
  StyledActionsBox,
  StyledToolbarButton
} from './AutomaticRosteringActions.styles';

/**
 * The two automatic-rostering actions, injected into the toolbar's `beforeDatePicker` slot.
 *
 * They carry labels rather than being icon-only toolbar actions, for two reasons: side by side they
 * read as one feature instead of two unrelated icons, and the explanation action no longer sits next
 * to the info action, where a question mark and an "i" in a circle are hard to tell apart at 17px.
 *
 * Docked vertically there is no room for a label, so both fall back to the icon button the rest of
 * the rail uses.
 */
const AutomaticRosteringActions = ({
  run,
  blockReason,
  hasFailedToStart,
  onGenerate,
  canExplain,
  isExplainOpen,
  onToggleExplain
}: AutomaticRosteringActionsProps) => {
  'use memo';

  const { t } = useTranslation('common', {
    keyPrefix: 'rosterPlan.automaticRostering'
  });
  const isVertical = useIsVerticalToolbar();
  const isRunning = isAutomaticRosteringInProgress(run.state);
  // The caller's block reason already covers a running calculation, but a button that says the engine
  // is busy must not be clickable regardless of what it was handed - a second click asks for a
  // second calculation.
  const isGenerateDisabled = blockReason !== null || isRunning;

  // A failed calculation puts the engine's own Dutch sentence in the tooltip: it is the only thing
  // that says why, and the button itself has no room for it. A rollout that went wrong can come
  // without one, and a failure must still say it failed rather than fall back to the label - but
  // only on a button the planner can press: "try again" on a disabled one hides why it is disabled.
  const generateTooltip = isRunning
    ? t('generate.running')
    : hasFailedToStart
    ? t('generate.startFailed')
    : run.state === 'FAILED' && run.message
    ? run.message
    : run.state === 'FAILED' && !blockReason
    ? t('generate.failed')
    : blockReason
    ? t(`generate.blocked.${blockReason}`)
    : t('generate.label');

  const explainTooltip = canExplain
    ? t('chat.open')
    : t('chat.blockedNothingToExplain');

  if (isVertical) {
    return (
      <StyledActionsBox isVertical>
        <Tooltip title={generateTooltip} placement="left">
          <StyledRelativeBox>
            <IconButton
              id="roster-plan-generate"
              type="wand-magic-sparkles"
              size="small"
              onClick={onGenerate}
              disabled={isGenerateDisabled}
              aria-label={t('generate.label')}
              data-tourid="roster-plan-generate"
            />
            {/* No number: the engine reports a state, not progress. */}
            {isRunning && <PulsingBadge color="primary" content="" />}
          </StyledRelativeBox>
        </Tooltip>
        <Tooltip title={explainTooltip} placement="left">
          <StyledRelativeBox>
            <IconButton
              id="roster-plan-automatic-rostering-explain"
              type="comments"
              size="small"
              onClick={onToggleExplain}
              disabled={!canExplain}
              aria-label={t('chat.open')}
              data-tourid="roster-plan-automatic-rostering-explain"
            />
          </StyledRelativeBox>
        </Tooltip>
      </StyledActionsBox>
    );
  }

  // describeChild on both: MUI's Tooltip labels its child by default, which would replace the
  // button's own visible text as the accessible name. With a label on screen the tooltip is the
  // supplement, not the name.
  return (
    <StyledActionsBox isVertical={false}>
      <Tooltip title={generateTooltip} describeChild>
        {/* A disabled button fires no events, so the tooltip needs an element that still does -
            without this the planner cannot read why generating is blocked. */}
        <StyledRelativeBox>
          <StyledToolbarButton
            id="roster-plan-generate"
            variant="outlined"
            size="small"
            color="inherit"
            startIcon={<Icon type="wand-magic-sparkles" fontSize="small" />}
            onClick={onGenerate}
            disabled={isGenerateDisabled}
            data-tourid="roster-plan-generate"
          >
            {isRunning
              ? t('generate.buttonLabelRunning')
              : t('generate.buttonLabel')}
          </StyledToolbarButton>
        </StyledRelativeBox>
      </Tooltip>
      <Tooltip title={explainTooltip} describeChild>
        {/* Same reason as above: a disabled button fires no events, so without a wrapper the planner
            cannot read why the explanation is unavailable. */}
        <StyledRelativeBox>
          <StyledToolbarButton
            id="roster-plan-automatic-rostering-explain"
            variant={isExplainOpen ? 'contained' : 'outlined'}
            size="small"
            color="inherit"
            startIcon={<Icon type="comments" fontSize="small" />}
            onClick={onToggleExplain}
            disabled={!canExplain}
            data-tourid="roster-plan-automatic-rostering-explain"
          >
            {t('chat.buttonLabel')}
          </StyledToolbarButton>
        </StyledRelativeBox>
      </Tooltip>
    </StyledActionsBox>
  );
};

export default AutomaticRosteringActions;
